import type { ReactNode } from 'react';
import {
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
} from '../../layouts/ListPageLayout/ListPageLayout';
import { SkeletonBox } from './SkeletonBox';
import styles from './ListPageSkeleton.module.css';

export interface ListPageSkeletonProps {
	sidebarWidth?: string;
	sidebarW1500?: string;
	sidebarW1300?: string;
	sidebarW1200?: string;
	stackAt?: 1100 | 1124;
	tabsCount?: number;
	headerActionsCount?: number;
	tableRows?: number;
	tableCols?: number;
	sidebarCardsCount?: 1 | 2;
	showSearch?: boolean;
	showSort?: boolean;
	showFilter?: boolean;
	showPagination?: boolean;
	sidebar?: ReactNode;
}

const keys = (count: number, prefix: string) =>
	Array.from({ length: count }, (_, index) => `${prefix}-${index}`);

export const ListPageSkeleton = ({
	sidebarWidth = '27rem',
	sidebarW1500 = '22rem',
	sidebarW1300 = '19rem',
	sidebarW1200 = '17rem',
	stackAt = 1124,
	tabsCount = 1,
	headerActionsCount = 2,
	tableRows = 8,
	tableCols = 5,
	sidebarCardsCount = 2,
	showSearch = true,
	showSort = true,
	showFilter = true,
	showPagination = true,
	sidebar,
}: ListPageSkeletonProps) => (
	<ListPageLayout
		sidebarWidth={sidebarWidth}
		sidebarW1500={sidebarW1500}
		sidebarW1300={sidebarW1300}
		sidebarW1200={sidebarW1200}
		stackAt={stackAt}
		sidebar={
			sidebar ?? (
				<ListPageCharts single={sidebarCardsCount === 1}>
					{keys(sidebarCardsCount, 'sidebar-card').map((key) => (
						<ListPageChartCard
							key={key}
							maxHeight={sidebarCardsCount === 1 ? '30rem' : undefined}
						>
							<div className={styles.chartBody} />
						</ListPageChartCard>
					))}
				</ListPageCharts>
			)
		}
	>
		<div className={styles.card}>
			<div className={styles.header}>
				<div className={styles.headerLeft}>
					<SkeletonBox height="1.75rem" width="14rem" />
					<SkeletonBox height="1.2rem" width="22rem" />
				</div>
				{headerActionsCount > 0 && (
					<div className={styles.headerActions}>
						{keys(headerActionsCount, 'action').map((key) => (
							<SkeletonBox key={key} height="2.5rem" width="7.5rem" borderRadius="2rem" />
						))}
					</div>
				)}
			</div>

			<div className={styles.tabs}>
				{keys(tabsCount, 'tab').map((key, index) => (
					<div key={key} className={index === 0 ? styles.tabActive : styles.tab}>
						<SkeletonBox height="0.9rem" width="4.5rem" />
					</div>
				))}
			</div>

			{(showSearch || showSort || showFilter) && (
				<div className={styles.filters}>
					{showSearch && (
						<div className={styles.search}>
							<SkeletonBox height="1rem" width="1rem" borderRadius="50%" />
							<SkeletonBox height="0.8rem" width="12rem" />
						</div>
					)}
					{showSort && <SkeletonBox height="2.3rem" width="2.625rem" borderRadius="0.5rem" />}
					{showFilter && <SkeletonBox height="2.3rem" width="5.75rem" borderRadius="0.5rem" />}
				</div>
			)}

			<div className={styles.table}>
				<div
					className={styles.tableHeader}
					style={{ gridTemplateColumns: `repeat(${tableCols}, minmax(0, 1fr))` }}
				>
					{keys(tableCols, 'th').map((key) => (
						<SkeletonBox key={key} height="0.75rem" width="70%" />
					))}
				</div>
				{keys(tableRows, 'row').map((rowKey) => (
					<div
						key={rowKey}
						className={styles.tableRow}
						style={{ gridTemplateColumns: `repeat(${tableCols}, minmax(0, 1fr))` }}
					>
						{keys(tableCols, `${rowKey}-col`).map((colKey, colIndex) => (
							<SkeletonBox
								key={colKey}
								height="0.9rem"
								width={colIndex === 0 ? '80%' : '65%'}
							/>
						))}
					</div>
				))}
			</div>

			{showPagination && (
				<div className={styles.pagination}>
					<SkeletonBox height="2rem" width="2rem" borderRadius="0.375rem" />
					{keys(5, 'page').map((key) => (
						<SkeletonBox key={key} height="2rem" width="2rem" borderRadius="0.375rem" />
					))}
					<SkeletonBox height="2rem" width="2rem" borderRadius="0.375rem" />
				</div>
			)}
		</div>
	</ListPageLayout>
);
